import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { AllergyMark, AllergyResponse, ChildSummaryResponse, Gender } from '../api/types';
import { AllergenIcon, AllergyBadge } from '../allergens/AllergenIcon';
import { useI18n } from '../i18n/I18nProvider';
import { Avatar, type AvatarSize } from './Photo';
import { Badge, type BadgeTone } from './primitives';

/**
 * Today's presence, derived from the attendance record (FR-HOME-02).
 *
 * Four states, each with its own colour and its own wording, because "no record yet" and "reported absent"
 * mean very different things to a parent looking at their phone at ten in the morning.
 */
export type Presence =
  | { kind: 'present' }
  | { kind: 'departed'; at: string }
  | { kind: 'absenceReported' }
  | { kind: 'notArrived' };

export function PresenceLabel({ presence, gender }: { presence: Presence; gender?: Gender | null }) {
  const { tg, t, formatTime } = useI18n();

  const { modifier, text } = (() => {
    switch (presence.kind) {
      case 'present':
        return { modifier: 'present', text: tg('presence.present', gender) };
      case 'departed':
        // The gendered verb and the time come from one message, never concatenated (NFR-I18N-01).
        return { modifier: 'departed', text: tg('presence.departed', gender, { time: formatTime(presence.at) }) };
      case 'absenceReported':
        return { modifier: 'absent', text: t('presence.absenceReported') };
      case 'notArrived':
        return { modifier: 'unknown', text: tg('presence.notArrived', gender) };
    }
  })();

  return (
    <span className={`dg-presence dg-presence--${modifier}`}>
      <span className="dg-presence__dot" aria-hidden="true" />
      <span>{text}</span>
    </span>
  );
}

/**
 * A child's profile photo, or their initial where there is none.
 *
 * The profile photo is for recognising the child, so it reaches only staff who can see the child and the
 * child's own guardians; the API decides that and sends `photoUrl` only to them (decision 2026-09-23 #19).
 *
 * An allergy travels with the child's face: a small circle at the photo's lower corner on the reading end (the left
 * in Arabic) shows the most serious allergen, and its tooltip names every allergy and how serious it is (Q75, Q76).
 */
export function ChildAvatar({ child, photoUrl, size = 'md', allergies }: {
  child: Pick<ChildSummaryResponse, 'firstName' | 'preferredName'>;
  photoUrl?: string | null;
  size?: AvatarSize;
  /** The child's allergies: a list row's marks, or the child's own page's allergies. */
  allergies?: readonly Pick<AllergyMark, 'allergen' | 'severity' | 'icon'>[];
}) {
  const avatar = <Avatar name={child.preferredName || child.firstName} photoUrl={photoUrl} size={size} />;
  if (!allergies?.length) return avatar;

  return (
    <span className={`dg-child-avatar dg-child-avatar--${size}`}>
      {avatar}
      <AllergyBadge allergies={allergies} />
    </span>
  );
}

export interface ChildCardProps {
  /**
   * Without the consent flags or the critical-allergy flag, which the card never shows (the allergy is on the photo,
   * Q75, Q76): a board builds it from rows that do not carry them.
   */
  child: Omit<ChildSummaryResponse, 'individualPhotoConsent' | 'groupPhotoConsent' | 'hasCriticalAllergy'>;
  photoUrl?: string | null;
  presence?: Presence;
  /** Where selecting the child leads. A link rather than a button: it is navigation, and it deep-links. */
  to?: string;
  onSelect?: () => void;
  /** A line under the name — the class, the nursery, or what is still missing for today. */
  meta?: ReactNode;
  trailing?: ReactNode;
}

/**
 * One child, as a card.
 *
 * Shared by the guardian's child chooser (FR-SEL-02) and the teacher's class list (FR-STF-02), which want
 * the same thing: a face, a name, and today's status, tappable with a thumb.
 */
export function ChildCard({ child, photoUrl, presence, to, onSelect, meta, trailing }: ChildCardProps) {
  const displayName = child.preferredName
    ? `${child.preferredName} ${child.lastName}`
    : `${child.firstName} ${child.lastName}`;

  const body = (
    <>
      <ChildAvatar child={child} photoUrl={photoUrl} allergies={child.allergyMarks} />
      <span className="dg-child-card__body">
        <span className="dg-child-card__name">{displayName}</span>
        {meta && <span className="dg-child-card__meta">{meta}</span>}
        {presence && <PresenceLabel presence={presence} gender={child.gender} />}
      </span>
      <span className="dg-spacer" />
      {trailing}
    </>
  );

  if (to) return <Link className="dg-child-card" to={to}>{body}</Link>;
  return <button type="button" className="dg-child-card" onClick={onSelect}>{body}</button>;
}

/**
 * The allergy warning shown wherever food is recorded (FR-STF-07).
 *
 * Not dismissible, and it names the allergen rather than saying "this child has an allergy": a teacher
 * about to hand over a plate needs the word, not a flag.
 */
export function AllergyWarning({ allergies }: { allergies: AllergyResponse[] }) {
  const { t } = useI18n();
  const serious = allergies.filter((a) => a.requiresAcknowledgement);
  if (serious.length === 0) return null;

  return (
    <div className="dg-allergy-warning" role="alert">
      <p>{t('allergy.warning.title')}</p>
      {/* The picture beside the word, never instead of it (Q75). */}
      <ul className="dg-allergy-warning__list">
        {serious.map((allergy) => (
          <li key={allergy.id} className="dg-allergy-warning__item">
            <AllergenIcon icon={allergy.icon} allergen={allergy.allergen} severity={allergy.severity} />
            <span>
              <strong>{allergy.allergen}</strong>
              {allergy.actionRequired ? ` — ${allergy.actionRequired}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A tenant or child status, in the active language and in the colour that matches its seriousness. */
export function StatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const tones: Record<string, BadgeTone> = {
    Active: 'success', Enrolled: 'success',
    Trial: 'info', Waitlisted: 'info', Invited: 'info',
    PastDue: 'warning',
    Suspended: 'danger',
    Archived: 'neutral', Withdrawn: 'neutral', Graduated: 'neutral', Deactivated: 'neutral',
  };
  return <Badge tone={tones[status] ?? 'neutral'}>{t(`status.${status}`)}</Badge>;
}
